import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { ThemeId, ThemeContextValue } from './types';
import { THEMES, THEME_MAP, DEFAULT_THEME_ID } from './themes';
import { loadStoredTheme, saveStoredTheme, isValidThemeId } from './themeStorage';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export interface ThemeProviderProps {
  children: React.ReactNode;
  initialTheme?: ThemeId;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialTheme,
}) => {
  const [theme, setThemeState] = useState<ThemeId>(() => {
    if (initialTheme && isValidThemeId(initialTheme)) {
      return initialTheme;
    }
    return loadStoredTheme();
  });

  const setTheme = useCallback((newTheme: ThemeId) => {
    if (isValidThemeId(newTheme)) {
      setThemeState(newTheme);
      saveStoredTheme(newTheme);
    }
  }, []);

  // Synchronize theme to document element
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    const themeDef = THEME_MAP[theme] || THEME_MAP[DEFAULT_THEME_ID];
    if (themeDef.type === 'light') {
      root.classList.add('theme-light');
      root.classList.remove('dark', 'theme-dark');
      root.style.colorScheme = 'light';
    } else {
      root.classList.add('dark', 'theme-dark');
      root.classList.remove('theme-light');
      root.style.colorScheme = 'dark';
    }
  }, [theme]);

  const currentTheme = useMemo(() => THEME_MAP[theme] || THEME_MAP[DEFAULT_THEME_ID], [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      currentTheme,
      availableThemes: THEMES,
      setTheme,
    }),
    [theme, currentTheme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
